import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthProvider";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { MASCARA_CEP, MASCARA_CPF, MASCARA_TELEFONE } from "@/utils/Constants";
import EnderecoService from "@/services/EnderecoService";
import { InputFormik } from "@/components/ui/input-formik";
import { InputMask } from "@/components/ui/input-mask";
import { EnderecoDto } from "@/dtos/EnderedoDto";
import AcessoService, { CadastrarDto } from "@/services/AcessoService";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import CidadesService, { CidadeDto } from "@/services/CidadesService";
import Select from 'react-select';
import { labelPluralLower } from "@/utils/copyTituloBilhete";
import { normalizarCPF, validarCPF } from "@/utils/Cpf";
import { validarNomeCompleto } from "@/utils/NomeCompleto";

interface CadastrarClienteDto {
    nome: string,
    cpf: string,
    nomeSocial: string,
    telefone: string,
    email: string,
    senha: string,
    confirmarSenha: string,
    termos: boolean
}

export function AutenticacaoPage() {
    const { realizarLogin, isAuthorized, cadastrarCliente } = useAuth();
    const navigate = useNavigate();
    const urlRegulamento = import.meta.env.VITE_APP_URLREGULAMENTO;
    let [searchParams, setSearchParams] = useSearchParams();
    const { toast } = useToast();
    const [realizandoCadastro, setRealizandoCadastro] = useState(false);
    const [exibirAlerta, setExibirAlerta] = useState(false);
    const [endereco, setEndereco] = useState<EnderecoDto>();
    const [buscandoCadastroPorCpf, setBuscandoCadastroPorCpf] = useState(false);
    const [loginValidado, setLoginValidado] = useState(false);
    const [validandoLogin, setValidandoLogin] = useState(false);
    const [cidadeSelecionada, setCidadeSelecionada] = useState<string>('');
    const [cidades, setCidades] = useState<CidadeDto[]>([]);
    const cidadesSelect = [{ value: '', label: 'Selecione sua Cidades' }, ...cidades.map(cidade => ({ value: cidade.cidade, label: cidade.cidade }))];


    useEffect(() => {
        if (isAuthorized) {
            navigate('/compra/titulos')
        }

        const searchParamsRealizandoCadastro = searchParams.get('realizandoCadastro')

        if (searchParamsRealizandoCadastro !== null) {
            setRealizandoCadastro(true);

            if (searchParamsRealizandoCadastro) {
                BuscarPorCep(searchParamsRealizandoCadastro);
            }
        }

        formikFormCep.validateForm();
        buscarCidades();
    }, [])

    async function buscarCidades() {
        const cidadess = await CidadesService.buscarCidades();
        setCidades(cidadess);
    }

    useEffect(() => {
        const searchParamsRealizandoCadastro = searchParams.get('realizandoCadastro')

        if (searchParamsRealizandoCadastro !== null) {
            setRealizandoCadastro(true);
            if (searchParamsRealizandoCadastro) {
                formikFormCep.setFieldValue('cep', searchParamsRealizandoCadastro);
            }
        } else {
            setRealizandoCadastro(false);
            setEndereco(undefined);
            formikFormCep.resetForm();
        }
    }, [searchParams]);

    const formikFormAutenticar = useFormik({
        initialValues: {
            login: '',
            senha: ''
        },
        validationSchema: Yup.object({
            login: Yup.string()
                .min(5, 'Deve possuir ao menos 5 caracteres')
                .required('Obrigatório'),
            senha: Yup.string()
                .min(5, 'Deve possuir ao menos 5 caracteres')
                .required('Obrigatório')
        }),
        onSubmit: async (values) => {
            try {
                await realizarLogin(values.login, values.senha);
                toast({
                    variant: 'success',
                    description: 'Autenticado com sucesso!',
                    duration: 3000
                })

                if (searchParams.get('redirect')) {
                    navigate('/compra/finalizar')
                    return;
                }

                navigate('/compra/titulos')
            } catch (error: any) {
                toast({
                    variant: 'destructive',
                    description: error,
                    duration: 4000
                });
            }
        }
    });

    async function BuscarPorCep(cep: string) {
        try {
            const { Data: result } = await EnderecoService.buscarPorCep(cep);
            setEndereco(result);
        } catch (error: any) {
            toast({
                variant: 'destructive',
                description: error,
                duration: 4000
            });
            throw error;
        }
    }

    const formikFormCep = useFormik({
        initialValues: {
            cep: ''
        },
        validationSchema: Yup.object({
            cep: Yup.string()
        }),
        onSubmit: async (values, { setStatus }) => {
            setStatus(undefined);

            const cep = values.cep.replace(/_/g, '').replace(/-/g, '')

            if (cep.length !== 8) {
                formikFormCep.setStatus('Digite um CEP válido')
                return;
            }

            try {
                await BuscarPorCep(cep)
            } catch (error: any) {
                setStatus(error);
                return;
            }

            try {
                let valores = { realizandoCadastro: cep } as any
                if (searchParams.get('redirect')) {
                    valores = { ...valores, redirect: '/compra/finalizar' }
                }

                setSearchParams(valores)
            } catch (error: any) {
                toast({
                    variant: 'destructive',
                    description: error,
                    duration: 4000
                });
            }
        }
    });

    const formikFormConfirmarCadastro = useFormik<CadastrarClienteDto>({
        initialValues: {
            nome: '',
            nomeSocial: '',
            cpf: '',
            telefone: '',
            email: '',
            senha: '',
            confirmarSenha: '',
            termos: false
        },
        validationSchema: Yup.object({
            nome: Yup.string()
                .required('Obrigatório')
                .test('Validar Nome Completo', 'Informe o nome completo', validarNomeCompleto),
            nomeSocial: Yup.string(),
            cpf: Yup.string()
                .required('Obrigatório')
                .test('Validar CPF', 'CPF Inválido', cpfToValidate => {
                    return validarCPF(cpfToValidate || '');
                }),
            telefone: Yup.string()
                .required('Obrigatório')
                .test('Validar Telefone', 'Telefone Inválido', telefoneToValidate => {
                    if (telefoneToValidate
                        .replace(/_/g, '')
                        .replace(/\(/g, '')
                        .replace(/\)/g, '')
                        .replace(/-/g, '')
                        .replace(/\./g, '')
                        .replace(/\s/g, '')
                        .length !== 11) {
                        return false;
                    }

                    return true;
                }),
            email: Yup.string().email('Digite um email Válido').required('Obrigatório'),
            senha: Yup.string().required('Obrigatório'),
            confirmarSenha: Yup.string()
                .min(5, 'Deve possuir ao menos 5 caracteres')
                .required('Obrigatório')
                .test('Validar Confirmar Senha', 'Digite as duas senhas iguais', passwordToValidate => {
                    if (passwordToValidate !== formikFormConfirmarCadastro.values.senha) {
                        return false;
                    }

                    return true;
                }),
            termos: Yup.bool()
                .required('Os Termos de Uso devem ser aceito').oneOf([true], 'Os Termos de Uso devem ser aceito')
        }),
        onSubmit: async (values) => {
            const cadastrarValues: CadastrarDto = {
                Nome: values.nome,
                NomeSocial: values.nomeSocial,
                CPF: normalizarCPF(values.cpf),
                Telefone: values.telefone
                    .replace(/_/g, '')
                    .replace(/\(/g, '')
                    .replace(/\)/g, '')
                    .replace(/-/g, '')
                    .replace(/\./g, '')
                    .replace(/\s/g, ''),
                Email: values.email,
                Senha: values.senha,
                // @ts-ignore
                Endereco: endereco
            }

            try {
                await cadastrarCliente(cadastrarValues);
                toast({
                    description: 'Cadastro Realizado com sucesso!',
                    duration: 3000,
                    variant: 'success'
                })

                if (searchParams.get('redirect')) {
                    navigate('/compra/finalizar')
                    return;
                }

                navigate('/')
            } catch (error: any) {
                toast({
                    variant: 'destructive',
                    description: error,
                    duration: 4000
                });
            }
        }
    });

    async function handleValidarLogin() {
        try {
            setValidandoLogin(true)
            await realizarLogin(formikFormAutenticar.values.login, '');
            setLoginValidado(true);
        } catch (error: any) {
            if (error === 'Identificamos que é seu primeiro acesso, cadastre uma senha') {
                navigate('/PrimeiroAcesso?login=' + formikFormAutenticar.values.login)
            } else {
                setLoginValidado(true);
            }
        } finally {
            setValidandoLogin(false)
        }
    }

    useEffect(() => {
        async function buscarCadastroPorCpf() {
            try {
                const cpf = normalizarCPF(formikFormConfirmarCadastro.values.cpf);

                if (cpf.length !== 11) {
                    return;
                }

                if (!validarCPF(cpf)) {
                    return;
                }

                setBuscandoCadastroPorCpf(true);
                toast({
                    description: 'Pesquisando cadastro pelo CPF, aguarde um momento...',
                    duration: 3000,
                    variant: 'warning'
                })

                const { Data: result } = await AcessoService.buscarCadastroPorCPF(cpf);

                formikFormConfirmarCadastro.setValues(
                    {
                        nome: result.Nome || '',
                        nomeSocial: result.NomeSocial || '',
                        telefone: result.Telefone || '',
                        email: result.Email || '',
                        senha: formikFormConfirmarCadastro.values.senha || '',
                        confirmarSenha: formikFormConfirmarCadastro.values.confirmarSenha || '',
                        cpf: formikFormConfirmarCadastro.values.cpf || '',
                        termos: false
                    }
                )
            } catch (error: any) {
                if (error === 'Cliente já cadastrado, utilize a recuperação de senha.') {
                    setExibirAlerta(true);
                }
            } finally {
                setBuscandoCadastroPorCpf(false);
            }
        }

        buscarCadastroPorCpf();



    }, [formikFormConfirmarCadastro.values.cpf])

    function handleCadastrarCepOuSelecionarCidade(): void {
        formikFormCep.setStatus(undefined)
        if (cidadeSelecionada) {
            try {
                let valores = { realizandoCadastro: '', cidade: cidadeSelecionada } as any
                if (searchParams.get('redirect')) {
                    valores = { ...valores, redirect: '/compra/finalizar' }
                }

                setSearchParams(valores)
            } catch (error: any) {
                toast({
                    variant: 'destructive',
                    description: error,
                    duration: 4000
                });
            }

            const cidade = cidades.find(c => c.cidade == cidadeSelecionada);

            setEndereco({ Cidade: cidadeSelecionada, CEP: '', UF: cidade!.estado, Bairro: '' });
        } else {
            formikFormCep.handleSubmit();
        }
    }

    return (
        <Card className="mx-auto max-w-sm my-4">
            <AlertDialog open={exibirAlerta} onOpenChange={o => {
                if (!o) {
                    navigate('/EsqueciMinhaSenha')
                }
            }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-2xl text-primary-foreground">
                            Cliente já cadastrado, utilize a recuperação de senha.
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Você será redirecionado para a tela de recuperação de senha
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogAction>OK</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            <CardHeader>
                {
                    realizandoCadastro ? (
                        <CardTitle className="text-2xl text-primary-foreground">Cadastro</CardTitle>
                    ) : (
                        <CardTitle className="text-2xl text-primary-foreground">Login</CardTitle>
                    )
                }

                <CardDescription>
                    Realize seu cadastro para adquirir seus {labelPluralLower()} e concorrer a prêmios toda semana.
                </CardDescription>

            </CardHeader>
            <CardContent>


                {
                    !realizandoCadastro && (
                        <>
                            <form onSubmit={formikFormAutenticar.handleSubmit} className="grid gap-4 text-primary-foreground">
                                <div className="grid gap-2">
                                    <Label htmlFor="login">Email ou CPF</Label>
                                    <Input
                                        tabIndex={1}
                                        id="login"
                                        name="login"
                                        type="text"
                                        placeholder="Email ou CPF"
                                        required
                                        onChange={formikFormAutenticar.handleChange}
                                        value={formikFormAutenticar.values.login}
                                    />
                                    <span>{formikFormAutenticar.errors.login}</span>
                                    {
                                        !loginValidado && (
                                            <Link to="/EsqueciMinhaSenha" className="ml-auto inline-block text-sm underline text-primary">
                                                Esqueceu sua senha?
                                            </Link>
                                        )
                                    }
                                </div>
                                {
                                    loginValidado && (
                                        <div className="grid gap-2">
                                            <div className="flex items-center text-primary-foreground">
                                                <Label htmlFor="senha">Senha</Label>
                                                <Link to="/EsqueciMinhaSenha" className="ml-auto inline-block text-sm underline text-primary">
                                                    Esqueceu sua senha?
                                                </Link>
                                            </div>
                                            <Input
                                                tabIndex={2}
                                                id="senha"
                                                name="senha"
                                                type="password"
                                                placeholder="Senha"
                                                required
                                                disabled={!loginValidado}
                                                onChange={formikFormAutenticar.handleChange}
                                                value={formikFormAutenticar.values.senha}
                                            />
                                            <span>{formikFormAutenticar.errors.senha}</span>
                                        </div>
                                    )
                                }
                                {
                                    loginValidado ? (
                                        <Button
                                            disabled={formikFormAutenticar.errors.login !== undefined || formikFormAutenticar.errors.senha !== undefined || formikFormAutenticar.isSubmitting}
                                            tabIndex={3}
                                            type="submit"
                                            className="w-full">
                                            {formikFormAutenticar.isSubmitting ? 'Acessando...' : 'Acessar'}
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={handleValidarLogin}
                                            disabled={formikFormAutenticar.errors.login !== undefined || validandoLogin}
                                            tabIndex={3}
                                            type="button"
                                            className="w-full">
                                            Continuar
                                        </Button>
                                    )
                                }
                            </form>
                            <div className="my-4 text-center text-lg">
                                <span>É sua primeira compra?{" "}</span>
                            </div>
                        </>
                    )
                }

                <div className=" bg-red-600 rounded-md text-center mb-5">
                    <span className="text-lg text-white">
                        {
                            formikFormCep.status
                        }
                    </span>
                </div>

                {
                    !endereco && (
                        <form className="text-center text-lg">
                            <div className="grid gap-2 text-primary-foreground">
                                <div className="flex items-center text-primary-foreground">
                                    <Label htmlFor="cep">Digite seu CEP ou Selecione sua Cidade</Label>
                                </div>
                                <InputMask
                                    name="cep"
                                    mask={MASCARA_CEP}
                                    placeholder="Cep"
                                    type="text"
                                    id="cep"
                                    formik={formikFormCep}
                                />

                                <Select
                                    defaultValue={{ value: '', label: 'Selecione sua Cidade' }}
                                    options={cidadesSelect}
                                    onChange={async (e) => {
                                        setCidadeSelecionada(e!.value);
                                    }}
                                    isMulti={false}
                                    className='form-control text-start text-sm' />
                            </div>
                            <Button onClick={handleCadastrarCepOuSelecionarCidade} disabled={formikFormCep.isSubmitting} variant="ghost" type="button" className="w-full underline text-primary-foreground mt-2">
                                {
                                    formikFormCep.isSubmitting ? 'Carregando Informações...' : 'Cadastre-se'
                                }
                            </Button>
                        </form>
                    )
                }

                {
                    (realizandoCadastro && endereco) && (
                        <>
                            <form onSubmit={formikFormConfirmarCadastro.handleSubmit} className="mt-2">
                                <div className="grid gap-2 text-primary-foreground">

                                    <div className="flex items-center text-primary-foreground mt-2">
                                        <Label htmlFor="cpf">CPF</Label>
                                    </div>
                                    <InputMask
                                        disabled={buscandoCadastroPorCpf}
                                        placeholder="CPF"
                                        type="text"
                                        id="cpf"
                                        required
                                        formik={formikFormConfirmarCadastro}
                                        name='cpf'
                                        mask={MASCARA_CPF}
                                    />
                                    <div className="flex items-center text-primary-foreground">
                                        <Label htmlFor="nome">Nome Completo</Label>
                                    </div>
                                    <InputFormik
                                        disabled={buscandoCadastroPorCpf}
                                        placeholder="Nome Completo"
                                        type="text"
                                        id="nome"
                                        required
                                        formik={formikFormConfirmarCadastro}
                                        name='nome'
                                    />

                                    <div className="flex items-center text-primary-foreground mt-2">
                                        <Label htmlFor="nomeSocial">Nome Social</Label>
                                    </div>
                                    <InputFormik
                                        disabled={buscandoCadastroPorCpf}
                                        placeholder="Nome Social"
                                        type="text"
                                        id="nomeSocial"
                                        formik={formikFormConfirmarCadastro}
                                        name='nomeSocial'
                                    />
                                    <div className="flex items-center text-primary-foreground mt-2">
                                        <Label htmlFor="telefone">Telefone</Label>
                                    </div>
                                    <InputMask
                                        disabled={buscandoCadastroPorCpf}
                                        mask={MASCARA_TELEFONE}
                                        placeholder="Telefone"
                                        type="text"
                                        id="telefone"
                                        required
                                        formik={formikFormConfirmarCadastro}
                                        name='telefone'
                                    />
                                    <div className="flex items-center text-primary-foreground mt-2">
                                        <Label htmlFor="email">Email</Label>
                                    </div>
                                    <InputFormik
                                        disabled={buscandoCadastroPorCpf}
                                        placeholder="Email"
                                        type="email"
                                        id="email"
                                        required
                                        formik={formikFormConfirmarCadastro}
                                        name='email'
                                    />
                                    <div className="flex items-center text-primary-foreground mt-2">
                                        <Label htmlFor="senha">Senha</Label>
                                    </div>
                                    <InputFormik
                                        disabled={buscandoCadastroPorCpf}
                                        placeholder="Senha"
                                        type="password"
                                        id="senha"
                                        required
                                        formik={formikFormConfirmarCadastro}
                                        name='senha'
                                    />
                                    <div className="flex items-center text-primary-foreground mt-2">
                                        <Label htmlFor="confirmarSenha">Confirme sua Senha</Label>
                                    </div>
                                    <InputFormik
                                        disabled={buscandoCadastroPorCpf}
                                        placeholder="Digite sua senha novamente"
                                        type="password"
                                        id="confirmarSenha"
                                        required
                                        formik={formikFormConfirmarCadastro}
                                        name='confirmarSenha'
                                    />
                                </div>

                                <div className="flex gap-2 mt-5 justify-center items-center">
                                    <Checkbox checked={formikFormConfirmarCadastro.values.termos} onCheckedChange={() => {
                                        formikFormConfirmarCadastro.setFieldValue('termos', !formikFormConfirmarCadastro.values.termos)
                                    }} id="termos" />
                                    <span>
                                        Declaro ter 16 anos ou mais e aceito os <a href={urlRegulamento} target="_blank" rel="noopener noreferrer" className="underline text-primary">Termos de Uso e Regulamento</a>
                                    </span>
                                </div>

                                <Button disabled={!formikFormConfirmarCadastro.isValid || formikFormConfirmarCadastro.isSubmitting} variant="default" type="submit" className="w-full mt-4">
                                    {
                                        formikFormConfirmarCadastro.isSubmitting ? 'Cadastrando...' : 'Cadastre-se'
                                    }
                                </Button>
                            </form>
                        </>
                    )
                }
            </CardContent>
        </Card>
    )
}